import React, {useCallback, useEffect, useRef, useState} from 'react'

const DEFAULT_VALIDATE_DEBOUNCE_MS = 300;

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const isShallowEqual = (left, right) => {
    if (left === right) {
        return true;
    }

    if (!isPlainObject(left) || !isPlainObject(right)) {
        return false;
    }

    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);

    if (leftKeys.length !== rightKeys.length) {
        return false;
    }

    for (let i = 0; i < leftKeys.length; i += 1) {
        const key = leftKeys[i];
        if (left[key] !== right[key]) {
            return false;
        }
    }

    return true;
};

export function useForm(initialFValues, validateOnChange = false, validate, validateDebounceMs = DEFAULT_VALIDATE_DEBOUNCE_MS) {

    const [values, setValues] = useState(initialFValues);
    const [errors, setErrorsState] = useState({});
    const [disabled, setDisabled] = useState({});

    const validateRef = useRef(validate);
    validateRef.current = validate;

    const debounceTimerRef = useRef(null);

    const scheduleValidate = useCallback((fieldValues) => {
        if (!validateOnChange || !validateRef.current) {
            return;
        }

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            validateRef.current(fieldValues);
        }, validateDebounceMs);
    }, [validateOnChange, validateDebounceMs]);

    useEffect(() => () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    }, []);

    const handleInputChange = useCallback((e) => {
        const {name, value} = e.target;
        setValues(prev => ({
            ...prev,
            [name]: value
        }));
        scheduleValidate({[name]: value});
    }, [scheduleValidate]);

    const handleSelectMultiChange = useCallback((e, options) => {
        const {name, value} = e.target;
        setValues(prev => ({
            ...prev,
            [name]: options.filter((opt) =>
                value.some((ae) => opt.id === ae.id))
        }));
        scheduleValidate({[name]: value});
    }, [scheduleValidate]);

    const setErrors = useCallback((nextErrors) => {
        setErrorsState((prevErrors) => {
            const resolvedErrors = typeof nextErrors === 'function'
                ? nextErrors(prevErrors)
                : nextErrors;

            if (isShallowEqual(prevErrors, resolvedErrors)) {
                return prevErrors;
            }

            return resolvedErrors;
        });
    }, []);

    const resetForm = () => {
        setValues(initialFValues);
        setErrors({})
    };

    return {
        values,
        setValues,
        errors,
        setErrors,
        disabled,
        setDisabled,
        handleInputChange,
        handleSelectMultiChange,
        resetForm

    }
}

export function Form(props) {

    const {children, ...other} = props;
    return (
        <form autoComplete="off" {...other}>
            {props.children}
        </form>
    )
}
